import dayjs from 'dayjs';
import { FC, useEffect, useRef } from 'react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { ISO8601 } from '../support/ISO8601';
import { CheckinState, ISO8601Month } from './model/CheckinState';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { JournalNav } from './summary/JournalNav';
import { JournalCalendar } from './summary/JournalCalendar';
import { CareRecipientSwitcher } from '../navigation/CareRecipientSwitcher';
import { JournalOverviewCards } from './summary/JournalOverviewCards';
import { FrequencyCard } from './summary/FrequencyCard';
import { CareTimeTotalsCard } from './summary/CareTimeTotalsCard';
import { useBreakpoints } from '../theme/useBreakpoints';
import { FullWidthHeader } from '../components/FullWidthHeader';
import { CalendarTabsProvider } from '../appointments/CalendarTabsProvider';
import { InnerhivePage } from '../InnerhivePage';
import { ICareCheckin, ICheckin, IPersonalCheckin } from './model/ICheckin';
import { IPeriodData } from './model/IPeriodData';
import { EntryCount } from './summary/EntryCount';
import { EntryStreak } from './summary/EntryStreak';
import { CalendarFooter } from './summary/CalendarFooter';
import { HeaderTabsState } from '../HeaderTabs';
import { FeelingChartCard } from './summary/FeelingChartCard';
import { WithWaypoint } from './components/WithWaypoint';
import { CareMapState } from '../caremap/CareMapState';

export const JournalPage: FC = () => {
    const { isAboveBreakpoint } = useBreakpoints();

    const checkinState = useInstance(CheckinState);
    useSubscription(() => checkinState);

    const mixpanel = useInstance(MixPanelEventEmitter);

    const tabsProvider = useInstance(CalendarTabsProvider);
    const tabsState = useInstance(HeaderTabsState);
    const careMapState = useInstance(CareMapState);

    const innerhivePageContentRef = useRef<HTMLIonContentElement>(null);

    useEffect(() => {
        const lastTab = tabsState.getLastActiveTab('calendar-tabs');
        if (!lastTab || lastTab.dataId === 'journal-tab') {
            mixpanel.track('Daily Log Calendar Opened');
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const onMonthChange = (month: ISO8601Month) => {
        const oldMonth = checkinState.getSelectedMonth();

        checkinState.selectMonth(month);

        mixpanel.track('Journal Calendar Month Switched', () => ({
            Direction: (month > oldMonth) ? 'Forward' : 'Backward'
        }));
    };

    const onSelect = (date: string) => {
        // can't create a checkin for a future day
        const isFuture = dayjs(date).startOf('day').isAfter(dayjs());
        if (isFuture) {
            return;
        }

        checkinState.startEditing({ date, trackingFrom: 'calendar' });

        mixpanel.track('Daily Log Opened', () => ({
            'Day': mixpanel.createMixpanelDateWithoutTime(dayjs(date).format('YYYY-MM-DD')),
            'From': 'calendar'
        }));
    };

    const getDateRange = (month: ISO8601Month) => {
        return {
            // don't use startOf('month') - it gives the wrong answer during daylight saving
            start: month.substring(0, 7) + '-01',
            end: dayjs(month).endOf('month').format('YYYY-MM-DD')
        };
    };

    const getDateFilter = ({ start, end }: { start: ISO8601, end: ISO8601 }) => {
        return (c: ICheckin) => {
            const isOnOrAfterStar = (c.timestamp.substring(0, 10) >= start.substring(0, 10));
            const isOnOrBeforeEnd = (c.timestamp.substring(0, 10) <= end.substring(0, 10));
            return isOnOrAfterStar && isOnOrBeforeEnd;
        };
    };

    const getMonthData = (personal: IPersonalCheckin[], care: ICareCheckin[], month: ISO8601): IPeriodData => {
        const range = getDateRange(month);
        const filter = getDateFilter(range);
        return {
            personal: personal.filter(filter),
            care: care.filter(filter),
            days: dayjs(range.end).diff(range.start, 'days') + 1
        };
    };

    const personalCheckins = checkinState.getPersonalCheckins();
    const careCheckins = checkinState.getCareCheckins() || [];

    // transition mid-month (15th) so daylight saving can't push us into the wrong month
    const selectedMonth = checkinState.getSelectedMonth();
    const priorMonth = dayjs(selectedMonth).date(15).subtract(1, 'month').format('YYYY-MM');

    const selectedMonthData = getMonthData(personalCheckins, careCheckins, selectedMonth);
    const priorMonthData = getMonthData(personalCheckins, careCheckins, priorMonth);

    const activeCaremap = careMapState.getActiveCaremap();

    // undo ionic variable margin to keep switcher consistent distance from fixed header
    const marginTop = (isAboveBreakpoint('md') ? -16 : 0);

    return (
        <InnerhivePage title="Calendar" tabs={tabsProvider.getTabs(activeCaremap)} contentRef={innerhivePageContentRef}>
            <div
                data-id="checkin-content"
                style={{paddingBottom: 20}}
            >
                <FullWidthHeader fixed>
                    <JournalNav
                        month={selectedMonth}
                        onMonthChange={onMonthChange}
                    />
                </FullWidthHeader>

                <div style={{margin: `${marginTop}px 6px 25px`}}>
                    <CareRecipientSwitcher trackingFrom="Journal" />
                </div>

                <JournalCalendar
                    month={selectedMonth}
                    onDayClick={onSelect}
                />

                <CalendarFooter>
                    <EntryCount selectedPeriod={selectedMonthData} />
                    <EntryStreak personalCheckins={personalCheckins} careCheckins={careCheckins} />
                </CalendarFooter>

                <WithWaypoint
                    scrollingElRef={innerhivePageContentRef}
                    trackingEvent="Dashboard Report Scrolled"
                >
                    <JournalOverviewCards
                        selectedPeriod={selectedMonthData}
                        priorPeriod={priorMonthData}
                    />
                </WithWaypoint>

                <div style={{display: 'flex', flexDirection: 'column'}}>

                    <WithWaypoint
                        scrollingElRef={innerhivePageContentRef}
                        trackingEvent="Daily Feeling Report Scrolled"
                    >
                        <FeelingChartCard
                            month={selectedMonth}
                            scrollerRef={innerhivePageContentRef}
                        />
                    </WithWaypoint>

                    <WithWaypoint
                        scrollingElRef={innerhivePageContentRef}
                        trackingEvent="Emotion Frequency Report Scrolled"
                    >
                        <FrequencyCard
                            data-id="emotions-card"
                            title="Emotion Frequency"
                            type="EMOTIONS"
                            checkins={selectedMonthData.personal}
                        />
                    </WithWaypoint>

                    <WithWaypoint
                        scrollingElRef={innerhivePageContentRef}
                        trackingEvent="Life Event Frequency Report Scrolled"
                    >
                        <FrequencyCard
                            data-id="life-events-card"
                            title="Life Event Frequency"
                            type="LIFE_EVENTS"
                            checkins={selectedMonthData.personal}
                        />
                    </WithWaypoint>

                    <WithWaypoint
                        scrollingElRef={innerhivePageContentRef}
                        trackingEvent="Care Time Report Scrolled"
                    >
                        <CareTimeTotalsCard
                            selectedPeriod={selectedMonthData}
                            priorPeriod={priorMonthData}
                        />
                    </WithWaypoint>

                </div>

            </div>
        </InnerhivePage>
    );
};
